import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, format, isAfter } from "date-fns";
import { laneLayoutForWeek, activityDayRange } from "@/lib/calendar-layout";

const MAX_LANES = 3;
const BAR_HEIGHT = 20;      // px, visible bar height
const BAR_GAP = 2;          // px, vertical gap between lanes
const DATE_ROW_HEIGHT = 32; // px, space reserved for the date number at the top

export default function CalendarMonth({ currentDate, activities, onDayClick, onActivityClick }) {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const weeks = [];
    for (let ws = gridStart; !isAfter(ws, gridEnd); ws = addDays(ws, 7)) {
        weeks.push(ws);
    }

    const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return (
        <div className="border-t border-l border-[#E5E5E3] bg-white" data-testid="calendar-month">
            <div className="grid grid-cols-7 border-b border-[#E5E5E3]">
                {weekdays.map(w => (
                    <div key={w} className="text-[10px] tracking-[0.2em] uppercase text-[#888885] font-medium p-3 border-r border-[#E5E5E3]">
                        {w}
                    </div>
                ))}
            </div>
            {weeks.map((weekStart, wi) => (
                <WeekRow
                    key={wi}
                    weekStart={weekStart}
                    activities={activities}
                    currentMonth={currentDate}
                    onDayClick={onDayClick}
                    onActivityClick={onActivityClick}
                />
            ))}
        </div>
    );
}

function WeekRow({ weekStart, activities, currentMonth, onDayClick, onActivityClick }) {
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const layout = laneLayoutForWeek(activities, weekStart);
    const visible = layout.filter(l => l.lane < MAX_LANES);

    // For "+N more" indicator per day
    const overflowByCol = {};
    for (const l of layout) {
        if (l.lane >= MAX_LANES) {
            for (let c = l.startCol; c < l.startCol + l.span; c++) {
                overflowByCol[c] = (overflowByCol[c] || 0) + 1;
            }
        }
    }

    // Index of activities touching each day (for data attributes used by tests)
    const idsPerCol = Array.from({ length: 7 }, () => []);
    for (const a of activities) {
        for (const d of activityDayRange(a)) {
            const diff = Math.round((d.getTime() - weekStart.getTime()) / 86400000);
            if (diff >= 0 && diff < 7) idsPerCol[diff].push(a.id);
        }
    }

    const barsAreaHeight = MAX_LANES * BAR_HEIGHT + (MAX_LANES - 1) * BAR_GAP;
    const rowMinHeight = DATE_ROW_HEIGHT + 4 + barsAreaHeight + 20; // + room for overflow indicator

    return (
        <div className="relative grid grid-cols-7" style={{ minHeight: rowMinHeight }}>
            {days.map((day, i) => {
                const key = format(day, "yyyy-MM-dd");
                const inMonth = isSameMonth(day, currentMonth);
                const today = isSameDay(day, new Date());
                const overflow = overflowByCol[i] || 0;
                return (
                    <div
                        key={i}
                        onClick={() => onDayClick?.(day)}
                        className={`border-r border-b border-[#E5E5E3] cursor-pointer hover:bg-[#FAFAF9] transition-colors ${inMonth ? "bg-white" : "bg-[#FAFAF9]"}`}
                        data-testid={`day-cell-${key}`}
                        data-event-ids={idsPerCol[i].join(",")}
                    >
                        <div className="flex items-start justify-between px-2 pt-2" style={{ height: DATE_ROW_HEIGHT }}>
                            <span
                                className={`text-sm ${inMonth ? "text-[#111110]" : "text-[#BBBBB8]"} ${today ? "bg-[#111110] text-white px-1.5 py-0.5 -ml-0.5" : ""}`}
                            >
                                {format(day, "d")}
                            </span>
                        </div>
                        {/* Space reserved for the bars overlay */}
                        <div style={{ height: barsAreaHeight }} aria-hidden="true" />
                        {overflow > 0 && (
                            <div className="text-[10px] text-[#888885] px-2 pb-1" data-testid={`day-overflow-${key}`}>
                                +{overflow} more
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Overlay grid of activity bars, aligned to the 7 day columns */}
            <div
                className="absolute inset-x-0 pointer-events-none grid grid-cols-7"
                style={{ top: DATE_ROW_HEIGHT + 4, columnGap: 0, rowGap: BAR_GAP, paddingLeft: 4, paddingRight: 4 }}
                aria-hidden="false"
            >
                {visible.map(({ activity, startCol, span, lane, continuesLeft, continuesRight }) => {
                    const bg = activity.color || "#F0F0EE";
                    return (
                        <button
                            key={`${activity.id}-${format(weekStart, "yyyyMMdd")}`}
                            onClick={e => { e.stopPropagation(); onActivityClick?.(activity); }}
                            style={{
                                backgroundColor: bg,
                                gridColumn: `${startCol + 1} / span ${span}`,
                                gridRow: lane + 1,
                                height: BAR_HEIGHT,
                                borderTopLeftRadius: continuesLeft ? 0 : undefined,
                                borderBottomLeftRadius: continuesLeft ? 0 : undefined,
                                borderTopRightRadius: continuesRight ? 0 : undefined,
                                borderBottomRightRadius: continuesRight ? 0 : undefined,
                            }}
                            className={`pointer-events-auto text-left text-xs leading-none px-1.5 truncate border-l-2 ${continuesLeft ? "border-l-0" : "border-[#111110]/15"} hover:brightness-95 transition-all flex items-center ${activity.is_past ? "opacity-50 line-through decoration-[#888885]/60 text-[#666664]" : "text-[#111110]"}`}
                            data-testid={`event-chip-${activity.id}`}
                            data-activity-id={activity.id}
                            data-span={span}
                            data-continues-left={continuesLeft ? "1" : "0"}
                            data-continues-right={continuesRight ? "1" : "0"}
                            title={activity.title}
                        >
                            {span === 1 && !continuesLeft ? (
                                <>
                                    <span className="text-[#666664] mr-1 no-underline">{format(new Date(activity.start_time), "HH:mm")}</span>
                                    <span className="truncate">{activity.title}</span>
                                </>
                            ) : (
                                <span className="truncate">
                                    {continuesLeft ? "… " : ""}{activity.title}{continuesRight ? " …" : ""}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
